import React, {useEffect, useMemo, useRef, useState} from "react";
import {Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import DraggableSwitch from "../../base/switch/draggable-switch";
import {Close} from "@mui/icons-material";
import classnames from "classnames";
import LazyImage from "../../app-specific/lazy-image";
import {ReactComponent as ImagePlaceholder} from "../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import {DatePicker} from "@mui/x-date-pickers";
import IconButton from "../../base/button/icon_button";
import useIsMounted from "../../../hooks/use-is-mounted";
import {TaskStatusTypes} from "../../../../core/constants/enums";
import Api from "../../../../core/services/api/api";
import DateUtils from "../../../../core/services/date-utils";
import useNoteBookTagsInfo from "../../../hooks/use-note-book-tags-info";
import {Link} from "react-router-dom";
import Chips, {ChipsContentType, ChipsTypes} from "../../base/chips/chips";
import CacheService from "../../../../core/services/cache/cache_service";
import {useOperatorInformation} from "../../../hooks/use-operator-information";


const TaskMoreInfoDialog = () => {
    const [loading, setLoading] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [completionDatePickerOpen, setCompletionDatePickerOpen] = useState(false);
    const [selectedDueDate, setSelectedDueDate] = useState(null);
    const [selectedCompletionDate, setSelectedCompletionDate] = useState(null);
    const dispatch = useDispatch();
    const anchorRef = useRef(null);
    const compAnchorRef = useRef(null);
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.taskMoreInformation] ?? {});
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();

    const [task, setTask] = useState(state.task ?? null);
    const {pageInfo, labelTags, loadingNotebookTagsInfo} = useNoteBookTagsInfo(task?.tags);

    const createdByProfileImageUrl = useMemo(() => CacheService.getOperatorProfileImage(task?.createdByOperator?.id), [task?.createdByOperator?.id]);
    const selfAssigned = useMemo(() => task?.createdByOperator?.id === operatorInformation?.id, [operatorInformation?.id, task?.createdByOperator?.id]);

    /**
     * With each change in the task and dialog being open, sets [task] in the inner state
     */
    useEffect(() => setTask(state.task ?? null), [state.task, state?.open]);

    /**
     * With each change in the task and dialog being open, sets [task] and [selectedDueDate] in the inner state
     */
    useEffect(() => {
        setSelectedDueDate(state.task?.dueDate ? DateUtils.serverToUTC(state.task?.dueDate) : undefined);
        setSelectedCompletionDate(state?.task?.completionDate ? DateUtils.serverToUTC(state.task?.completionDate) : undefined);
    }, [state.task, state?.open])

    /**
     * Closes the dialog by dispatching new state for the task more info dialog
     */
    const closeDialog = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.taskMoreInformation, {open: false}));
    }

    /**
     * Updates task being done or not
     * @param checked
     */
    const toggleTaskBeingDone = async (checked) => {
        setLoading(true);
        const response = await state.toggleTaskDoneStatus?.(task, checked);
        if (!isMounted())
            return;
        setLoading(false);
        if (response) {
            setTask(prevState => ({...prevState, taskStatus: response.data}));
        }
    }

    /**
     * Toggles date picker being open
     * @param {boolean} shouldOpen
     */
    const toggleDatePickerVisibility = (shouldOpen) => {
        setDatePickerOpen(shouldOpen);
    }


    /**
     * Toggles date picker being open for the completion date
     * @param {boolean} shouldOpen
     */
    const toggleCompletionDatePickerVisibility = (shouldOpen) => {
        setCompletionDatePickerOpen(shouldOpen);
    }

    /**
     * Handles changes in the date picker value
     */
    const handleDateChange = async (value) => {
        setSelectedDueDate(value);
        if (task.dueDate && value && DateUtils.serverToLocal(task.dueDate).isSame(DateUtils.UTCToLocal(value), 'day'))
            return;
        const response = await Api.updateTask({
            ...(task ?? {}),
            dueDate: value ? DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat) : null,
            updatedProperties: ['DueDate']
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            state.setTask?.(response?.data);
            setTask(prevState => ({...prevState, ...response?.data}));
        }
    }

    /**
     * Handles changes in the date picker value for completion date
     */
    const handleCompletionDateChange = async (value) => {
        setSelectedCompletionDate(value);
        if (task.completionDate && value && DateUtils.serverToLocal(task.completionDate).isSame(DateUtils.UTCToLocal(value), 'day'))
            return;
        const response = await Api.updateTask({
            ...(task ?? {}),
            completionDate: value,
            updatedProperties: ['CompletionDate']
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            state.setTask?.(response?.data);
            setTask(prevState => ({...prevState, ...response?.data}));
        }
    }

    /**
     * Removes the selected due date for the task
     * @param e
     */
    const clearDueDate = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleDateChange(null).then();
    }


    /**
     * Removes the selected completion date for the task
     * @param e
     */
    const clearCompletionDate = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleCompletionDateChange(null).then();
    }

    return (
        <Dialog
            open={state.open}
            setOpen={closeDialog}
            maxWidth={'md'}
            className={'task-more-info-dialog'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-between m-3 w-100'>
                    <p className={`title`}>
                        {task?.title}
                    </p>
                    <DraggableSwitch
                        activeTitle={'DONE'}
                        inActiveTitle={'TO DO'}
                        size={4}
                        disabled={loading}
                        checked={task?.taskStatus?.statusType === TaskStatusTypes.Done}
                        onChange={(checked) => toggleTaskBeingDone(checked)}
                    />
                </Row>
                <Divider className='divider'/>
                <div className='my-2 w-100'/>
                <div className={'w-100 d-flex flex-column'}>
                    <div className={'page-link-container'}>
                        {
                            pageInfo &&
                            <Button
                                variant={'text'}
                                color={'primary'}
                                className={'link-button'}
                                component={pageInfo.url ? Link : 'button'}
                                {...(pageInfo.url && {
                                    to: pageInfo.url,
                                    target: '_blank',
                                })}
                                {...(!pageInfo.url && {
                                    onClick: pageInfo.action,
                                })}
                            >
                                {
                                    loadingNotebookTagsInfo
                                        ? "Navigating..."
                                        : pageInfo.title
                                }
                            </Button>
                        }
                        <p className={'created-date'}>
                            {!!task?.createdDateTime ? DateUtils.serverToLocal(task?.createdDateTime).format(DateUtils.dateFormat) : ''}
                        </p>
                    </div>
                    <div className={'mb-2'}/>
                    <p className={'task-description'}>
                        {task?.description}
                    </p>
                    <div ref={anchorRef} className={'assigner-date-section'}>
                        <div className={classnames('assigned-container', {'self-assigned': selfAssigned})}>
                            <div className={'assigner-image-container'}>
                                <LazyImage
                                    alt={'assigner image'}
                                    noImage={<ImagePlaceholder/>}
                                    src={createdByProfileImageUrl}
                                    height={27}
                                />
                            </div>
                            <p className={'assigner-text'}>
                                Assigned by <span
                                className={'assigner-name'}>{task?.createdByOperator?.firstname ?? ''} {task?.createdByOperator?.lastname ?? ''}</span>
                            </p>
                        </div>
                        <DatePicker
                            slotProps={{
                                popper: {
                                    anchorEl: anchorRef.current,
                                },
                            }}
                            open={datePickerOpen}
                            disablePast
                            onClose={() => toggleDatePickerVisibility(false)}
                            value={selectedDueDate}
                            onChange={(date) => handleDateChange(date)}
                            slots={{
                                textField: (_props) => {
                                    const props = {..._props};
                                    delete props.ownerState;
                                    delete props.sx;
                                    return (
                                        <Button
                                            {...props}
                                            disabled={!selfAssigned}
                                            component={'span'}
                                            variant="outlined"
                                            className={
                                                classnames('due-date-button',
                                                    {
                                                        'passed': selectedDueDate?.isBefore(DateUtils.localNowToUTC(), 'day'),
                                                        'remained': !selectedDueDate?.isAfter(DateUtils.localNowToUTC(), 'day') && !!task?.isDone,
                                                    })
                                            }
                                            onClick={() => toggleDatePickerVisibility(true)}
                                        >
                                        <span className={'d-flex align-items-center justify-content-between'}>
                                            {
                                                (selfAssigned && !!selectedDueDate) &&
                                                <IconButton
                                                    component={'span'}
                                                    onPressed={(e) => clearDueDate(e)}
                                                    color={!!selectedDueDate && selectedDueDate?.isBefore(DateUtils.localNowToUTC(), 'day') ? 'paper.error' : 'secondary'}
                                                    className={'mr-1'}
                                                >
                                                    <Close className='icon extraSmall'/>
                                                </IconButton>
                                            }
                                            {
                                                !!selectedDueDate
                                                    ? `Due Date: ${DateUtils.UTCToLocal(selectedDueDate).format(DateUtils.dateFormat)}`
                                                    : selfAssigned
                                                        ? 'Add Due Date'
                                                        : 'This task has no due date'
                                            }
                                        </span>
                                        </Button>
                                    );
                                }
                            }}
                        />

                        {
                            task?.isNCRTask &&
                            <DatePicker
                                PopperProps={{
                                    anchorEl: compAnchorRef.current,
                                }}
                                open={completionDatePickerOpen}
                                disablePast
                                onClose={() => toggleCompletionDatePickerVisibility(false)}
                                value={selectedCompletionDate}
                                onChange={(date) => handleCompletionDateChange(date)}
                                slots={{
                                    textField: (_props) => {
                                        const props = {..._props};
                                        delete props.ownerState;
                                        delete props.sx;
                                        return (
                                            <Button
                                                {...props}
                                                disabled={!selfAssigned}
                                                component={'span'}
                                                variant="outlined"
                                                className={
                                                    classnames('due-date-button',
                                                        {
                                                            'passed': selectedCompletionDate?.isBefore(DateUtils.localNowToUTC(), 'day'),
                                                            'remained': !selectedCompletionDate?.isAfter(DateUtils.localNowToUTC(), 'day') && !!task?.isDone,
                                                        })
                                                }
                                                onClick={() => toggleCompletionDatePickerVisibility(true)}
                                            >
                                        <span className={'d-flex align-items-center justify-content-between'}>
                                            {
                                                (selfAssigned && !!selectedCompletionDate) &&
                                                <IconButton
                                                    component={'span'}
                                                    onPressed={(e) => clearCompletionDate(e)}
                                                    color={!!selectedCompletionDate && selectedCompletionDate?.isBefore(DateUtils.localNowToUTC(), 'day') ? 'paper.error' : 'secondary'}
                                                    className={'mr-1'}
                                                >
                                                    <Close className='icon extraSmall'/>
                                                </IconButton>
                                            }
                                            {
                                                !!selectedCompletionDate
                                                    ? `Due Date: ${DateUtils.UTCToLocal(selectedCompletionDate).format(DateUtils.dateFormat)}`
                                                    : selfAssigned
                                                        ? 'Add Due Date'
                                                        : 'This task has no due date'
                                            }
                                        </span>
                                            </Button>
                                        );

                                    }
                                }}
                                renderInput={(props) => (
                                    <Button
                                        {...props}
                                        disabled={!selfAssigned}
                                        variant="outlined"
                                        className={
                                            classnames('due-date-button ml-3')
                                        }
                                        onClick={() => toggleCompletionDatePickerVisibility(true)}
                                    >
                                        <div className={'d-flex align-items-center justify-content-between'}>
                                            {
                                                (selfAssigned && !!selectedCompletionDate) &&
                                                <IconButton
                                                    component={'span'}
                                                    onPressed={(e) => clearCompletionDate(e)}
                                                    color={'secondary'}
                                                    className={'mr-1'}
                                                >
                                                    <Close className='icon extraSmall'/>
                                                </IconButton>
                                            }
                                            {
                                                !!selectedCompletionDate
                                                    ? `Completion Date: ${DateUtils.UTCToLocal(selectedCompletionDate).format(DateUtils.dateFormat)}`
                                                    : selfAssigned
                                                        ? 'Add Completion Date'
                                                        : 'This task is not completed yet!'
                                            }
                                        </div>
                                    </Button>
                                )}
                            />
                        }
                    </div>
                    {
                        (!!labelTags?.length) &&
                        <div className={'d-flex flex-column mx-3'}>
                            <div className={'d-flex flex-wrap my-2'}>
                                <div className={'text-sm font-weight-semi-bold mr-2 mb-2'}>
                                    Tags:
                                </div>
                                {
                                    labelTags.map((tag) => (
                                        <Chips
                                            key={tag.key}
                                            type={ChipsTypes.none}
                                            contentType={ChipsContentType.text}
                                            content={`${tag.key}: ${tag.value ?? ""}`}
                                            className={'mr-2 mb-2'}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    }
                </div>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100'>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Button
                            disabled={!state.open || loading}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={closeDialog}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {loading ? 'loading...' : 'Close'}
                        </Button>
                        <div/>
                    </Row>
                </Row>
            </Row>
        </Dialog>
    );
}

export default TaskMoreInfoDialog;
